import React, { FC, useEffect } from 'react'
import {
  Route,
  Switch,
  useHistory,
  useLocation,
  useParams,
  useRouteMatch,
} from 'react-router-dom'

// Components
import { Document } from 'modules/shares/components/Document'
import DocumentHeader from 'modules/shares/components/DocumentHeader'
import DocumentSidebarLayout from 'containers/DocumentSidebarLayout'
import { CwvRoutes } from 'modules/shares/ComponentsWebView'
import { Flex, useForTablet, NotEmbedded, IsEmbedded } from '@sketch/components'
import ShareWorkspaceSidebar from '../components/ShareWorkspaceSidebar'
import Banner from 'containers/Banner'

import DocumentSidebar from '../components/DocumentSidebar'
import ShareReadyGuard from '../components/ShareReadyGuard'

// Styles
import { ResponsiveValues } from '@sketch/global-styles'
import { PageContent, BannerWrapper } from './DocumentView.styles'

// Hooks
import {
  useVersioning,
  isUpgradeToLatestNeeded,
  UpgradeToLatestVersion,
} from 'modules/versioning'
import { useHandleSettingsQueryParamIfNeeded } from './DocumentView.hooks'
import { useGetVersionQueryWithPolling } from '../operations'

// Context
import { DocumentLayoutContext } from './DocumentLayoutContext'
import {
  SearchComponentsContext,
  InspectContextProvider,
  SelectedGroupContextProvider,
  CustomGridContextProvider,
  ComponentDescriptionContextProvider,
} from '../ComponentsWebView/context'

// Constants
import { RouteParams, versionedRoutes } from '@sketch/constants'

// Utils
import { getVersionRenderStatus } from 'utils/renderStatus'
import { useURLQuerySearch } from 'utils'
import { CWV_ROUTES } from 'utils/route'

import { getGroupType } from '../ComponentsWebView/utils'
import DocumentMobileToolbar from '../components/DocumentMobileToolbar'
import Navbar from 'components/Navbar'
import SketchHomeButton from 'components/SketchHomeButton'
import { NavbarBackButtonItem } from '../components/EmbeddedDetailHeader/EmbeddedDetailHeader.styles'
import BackButton from '../components/BackButton'
import HeaderNavigation from '../components/HeaderNavigation'
import { AnnotationQueryVariablesProvider } from 'modules/annotations/context'

/**
 * CONSTANTS
 */
const COLUMNS_WITH_SIDEBAR: ResponsiveValues<number> = [1, 1, 1, 2, 3, 4, 5, 6]
const COLUMNS_WITHOUT_SIDEBAR: ResponsiveValues<number> = [
  2,
  2,
  2,
  3,
  4,
  5,
  6,
  7,
]

const PROTOTYPES_ROUTES = [
  versionedRoutes.SHARE_PROTOTYPES.LATEST.template(),
  versionedRoutes.SHARE_PROTOTYPES.VERSION.template(),
]

/**
 * TYPES
 */
interface SidebarLayoutRenderProps {
  isSidebarRightOpen: boolean
  toggleSidebarRight: () => void
  isSidebarLeftOpen: boolean
  setSidebarLeftOpen: React.Dispatch<React.SetStateAction<boolean>>
}

interface ComponentsProvidersProps {
  search: string
}

/**
 * COMPONENTS
 */
const ComponentsProviders: FC<ComponentsProvidersProps> = ({
  children,
  search,
}) => {
  // used to reset the scroll to 0 everytime a new search is inputted
  // so we don't get search results loading with the scroll all way down
  useEffect(() => {
    const scrollingContainer = document.getElementsByClassName(
      'ContentContainer'
    )[0]
    if (scrollingContainer) {
      scrollingContainer.scrollTop = 0
    }
  }, [search])

  return (
    <SearchComponentsContext.Provider value={{ search }}>
      <SelectedGroupContextProvider>
        <InspectContextProvider>
          <CustomGridContextProvider>
            <ComponentDescriptionContextProvider>
              {children}
            </ComponentDescriptionContextProvider>
          </CustomGridContextProvider>
        </InspectContextProvider>
      </SelectedGroupContextProvider>
    </SearchComponentsContext.Provider>
  )
}

const DocumentView: FC = () => {
  const history = useHistory()
  const location = useLocation<{
    emptySearch: boolean
  }>()
  const params = useParams<
    RouteParams<'SHARE_VIEW'> | RouteParams<'SHARE_PAGE_VIEW'>
  >()
  const { path } = useRouteMatch()

  // Search query param, shared for both artboards and components
  const [search, setSearch, debouncedSearch] = useURLQuerySearch(
    location,
    history
  )

  // Reset search when moving between pages, symbols, text styles, layer styles
  // and color variables. But we want to keep it when coming back from artboards
  useEffect(() => {
    if (location.state?.emptySearch) {
      setSearch('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path])

  const versioning = useVersioning()
  const {
    versionShortId,
    share,
    latestVersionId,
    isViewingLatestVersion,
  } = versioning
  const currentVersion = !versioning.loading ? versioning.currentVersion : null
  const { data, error } = useGetVersionQueryWithPolling(
    share.identifier,
    versionShortId
  )
  const documentName = share.name

  useEffect(() => {
    if (versioning.artboardThumbnail !== '') {
      // clear the artboard thumbnail
      versioning.setArtboardThumbnail('')
    }
  }, [versioning])

  // Handle potential deeplink to settings modal
  useHandleSettingsQueryParamIfNeeded(share, {
    current: { shortId: versionShortId },
    latest: { shortId: latestVersionId },
  })

  const isTabletAndBigger = useForTablet()

  if (isUpgradeToLatestNeeded(error)) {
    return <UpgradeToLatestVersion error={error} />
  }

  const parsedRenderStatus = getVersionRenderStatus(data?.share?.version)

  const CWVRoute = getGroupType(path)
  const isPrototypePage = PROTOTYPES_ROUTES.includes(path)

  const permanentPageIdURL = 'pageUUID' in params ? params.pageUUID : undefined
  const firstDocumentPageIdentifier =
    currentVersion?.document?.pages?.entries[0]?.uuid

  const permanentPageId =
    permanentPageIdURL || firstDocumentPageIdentifier || ''

  return (
    <AnnotationQueryVariablesProvider
      shareIdentifier={share.identifier}
      permanentPageId={permanentPageId}
    >
      <ComponentsProviders search={search}>
        <DocumentSidebarLayout
          title={documentName}
          darkBackground
          header={({
            toggleSidebarRight,
            isSidebarRightOpen,
            isSidebarLeftOpen,
            setSidebarLeftOpen,
          }: SidebarLayoutRenderProps) => (
            <>
              <NotEmbedded>
                <DocumentHeader
                  share={share}
                  setSidebarLeftOpen={setSidebarLeftOpen}
                  toggleSidebarRight={toggleSidebarRight}
                  isSidebarRightOpen={isSidebarRightOpen}
                  isViewingLatestVersion={isViewingLatestVersion}
                  userCanOpenInApp={
                    !!currentVersion?.document?.userCanOpenInApp
                  }
                />
              </NotEmbedded>
              <IsEmbedded>
                <Navbar>
                  <Navbar.Section>
                    <SketchHomeButton />

                    {isTabletAndBigger && (
                      <NavbarBackButtonItem>
                        <BackButton />
                      </NavbarBackButtonItem>
                    )}

                    <HeaderNavigation share={share} />
                  </Navbar.Section>
                </Navbar>
              </IsEmbedded>
            </>
          )}
          sidebarLeft={
            <ShareWorkspaceSidebar
              workspaceIdentifier={share.workspace.identifier}
            />
          }
          sidebarRight={({ toggleSidebarRight }: SidebarLayoutRenderProps) => (
            <NotEmbedded>
              <DocumentSidebar
                share={share}
                permanentPageId={
                  'pageUUID' in params ? params.pageUUID : undefined
                }
                userCanSeeComments={!isPrototypePage}
                userCanInspect={share.userCanInspect}
                toggleSidebar={toggleSidebarRight}
                CWVRoute={CWVRoute}
              />
            </NotEmbedded>
          )}
        >
          {({
            isSidebarRightOpen,
            toggleSidebarRight,
          }: SidebarLayoutRenderProps) => (
            <DocumentLayoutContext.Provider
              value={{
                isSidebarRightOpen: !!isSidebarRightOpen,
                toggleSidebarRight,
              }}
            >
              <PageContent>
                <ShareReadyGuard renderStatus={parsedRenderStatus}>
                  <BannerWrapper>
                    <Banner />
                  </BannerWrapper>
                  <Flex flex="1">
                    <Switch>
                      <CwvRoutes exact path={CWV_ROUTES} />
                      <Route>
                        <Document
                          columns={
                            isSidebarRightOpen
                              ? COLUMNS_WITH_SIDEBAR
                              : COLUMNS_WITHOUT_SIDEBAR
                          }
                          search={debouncedSearch}
                          showPrototypeArtboards={!debouncedSearch}
                        />
                      </Route>
                    </Switch>
                  </Flex>
                  {!isTabletAndBigger && (
                    <DocumentMobileToolbar items={['search']} />
                  )}
                </ShareReadyGuard>
              </PageContent>
            </DocumentLayoutContext.Provider>
          )}
        </DocumentSidebarLayout>
      </ComponentsProviders>
    </AnnotationQueryVariablesProvider>
  )
}

export default DocumentView
